import React, { Component } from 'react'
import { connect } from "react-redux"
import { SketchPicker, SwatchesPicker, GithubPicker } from "react-color"

import Color from "color"
import trans from "trans"

import {
    TextInput,
    css,
    Field,
    Theme,
    Grid,
    GridCell,
} from "@bdfx/mergado-ui-kit"


import { RandomProduct } from "../types"
import { CustomPalette, CustomPalettesFormType, Gradient, Palette, SettingsFormType } from "app/types/model-types"
import { IconGradients, IconPalette } from "app/icons"

import { GradientSvg } from "./fields";
import { isValidHEXColor } from "./palette-colors";
import { selectFormValues } from 'app/reducers/form'
import { debounce, find } from 'lodash'

interface Props {
    name?: string
    value?: string
    label?: string
    changeValue: Function
    changeGradient?: Function
    randomProduct: RandomProduct
    settingsForm: SettingsFormType
    palettes: Palette[]
    gradients: Gradient[]
    customPalettesForm: CustomPalettesFormType
    hideGradients?: boolean
    disablePopover?: boolean
    displayColorPicker?: boolean
    doNotScrollIntoFocus?: boolean
    notRequired?: boolean
    wrapperWidth?: number
}

interface State {
    displayColorPicker: boolean
    displayPalettes: boolean
    displayGradients: boolean
    isValidHEXColor: boolean
}

export interface ColorType {
    hex: string
    hsl: {
        h: number
        s: number
        l: number
    }
    hsv: {
        h: number
        s: number
        l: number
    }
    oldHue: number
    rgb: {
        r: number
        g: number
        b: number
        a: number
    }
    source: string
}

export class TransparentColor extends Component<Props, State> {
    constructor(props) {
        super(props);

        this.state = {
            displayColorPicker: props.displayColorPicker ?? false,
            displayPalettes: false,
            displayGradients: false,
            isValidHEXColor: isValidHEXColor(props.value, props.notRequired)
        };
    }

    handleFocusDebounced = debounce(() => {
        const { name, doNotScrollIntoFocus } = this.props

        const target: HTMLElement = document.getElementById('color-picker-' + name)
        if (target) {
            target.focus()
            if (!doNotScrollIntoFocus) {
                target.scrollIntoView()
            }
        }
    }, 200)

    handleClick = () => {
        if (!this.state.displayColorPicker) {
            this.handleFocusDebounced()
        }

        this.setState({ displayColorPicker: !this.state.displayColorPicker, displayPalettes: false, displayGradients: false })
    }

    handleClose = () => {
        this.setState({ displayColorPicker: false, displayGradients: false });
        this.handleFocusDebounced()
    }

    toggleDisplayPalettes = () => this.setState({ displayPalettes: !this.state.displayPalettes, displayGradients: this.state.displayPalettes })
    toggleDisplayGradients = () => this.setState({ displayGradients: !this.state.displayGradients, displayPalettes: this.state.displayGradients })

    handleChangeComplete = (color: ColorType, closeOnChange: boolean = false) => {
        const a = Math.round(color.rgb.a * 255).toString(16);
        const finalHexColor = (color.hex !== 'transparent' ? color.hex : '#ffffff') + (color.rgb.a !== 1 ? (a.length === 1 ? "0" + a : a) : '')

        const isValid = isValidHEXColor(finalHexColor, this.props.notRequired)
        this.setState({ isValidHEXColor: isValid })

        this.props.changeValue(finalHexColor)
        if (closeOnChange) {
            this.handleClose()
        }
    }

    handleChangeGradient = (gradient: Gradient) => {
        if (typeof this.props.changeGradient !== "undefined") {
            this.props.changeGradient(gradient)
        }
        this.setState({ displayGradients: false, displayPalettes: false, displayColorPicker: false })
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (this.props.value !== nextProps.value ||
            this.state.displayColorPicker !== nextState.displayColorPicker ||
            this.state.displayPalettes !== nextState.displayPalettes ||
            this.state.displayGradients !== nextState.displayGradients ||
            this.state.isValidHEXColor !== nextState.isValidHexColor
        ) {
            return true;
        }
        return false;
    }

    render() {
        const { displayColorPicker, displayPalettes, displayGradients } = this.state
        const { label, name, randomProduct, customPalettesForm, settingsForm, palettes, gradients, hideGradients, disablePopover, wrapperWidth } = this.props

        const { custom_palette_id, palette_id } = settingsForm

        const selectedPalette: Palette = palettes && palette_id ?
            find(palettes, (palettes: Palette) => {
                return palette_id === palettes.id
            }) : null


        let selectedCustomPalette: CustomPalette

        if (customPalettesForm && customPalettesForm.palettes && Array.isArray(customPalettesForm.palettes)) {
            selectedCustomPalette = find(customPalettesForm.palettes, (customPalette: CustomPalette) => {
                return custom_palette_id === customPalette.id
            })

            //not custom_palette_id defined or palette not found, get first:
            if (!selectedCustomPalette) {
                selectedCustomPalette = customPalettesForm.palettes[0] || null
            }
        }

        const background = this.props.value || "#ffffff";

        let textColor = "#333";
        try {
            textColor = Color(background).contrast(Color('#fff')) > 2.2 ? "#ffffff" : "#333";
        } catch (e) {
        }

        const randomProductColors = [...(randomProduct && randomProduct.palette ? randomProduct.palette : []), '#ffffff', 'transparent']

        return (
            <StyledField className={'colorpicker-wrap'}>
                <Grid cols={'auto'}>
                    <GridCell col={'1/-1'}>
                        <div id={`color-picker-wrapper-${name}`}>
                            <TextInput
                                label={label}
                                placeholder={trans('no_color')}
                                value={this.props.value || ''}
                                invalid={!this.state.isValidHEXColor}
                                error={!this.state.isValidHEXColor ? trans('invalid_color') : undefined}
                                style={{ " .m-textinput-input": { backgroundColor: background, color: textColor } }}
                                onChange={(value) => {
                                    const isValid = isValidHEXColor(value, this.props.notRequired)
                                    this.setState({ isValidHEXColor: isValid })
                                    if (isValid) {
                                        this.props.changeValue(value)
                                    }
                                }}
                                onClick={(e) => this.handleClick()}
                                onClear={() => this.props.changeValue(null)}
                            />
                        </div>
                    </GridCell>

                    <GridCell>
                        {displayColorPicker &&
                            <div id={'color-picker-' + name} style={{ position: 'absolute', zIndex: 1000, display: 'grid' }}>
                                {!disablePopover &&
                                    <Popover onClick={(e) => this.handleClick()} className={'popover'} />
                                }

                                <div>
                                    <div id='cover-wrapper' style={{width: `${wrapperWidth ? wrapperWidth : 200}px`}}>
                                        <SketchPicker
                                            color={background}
                                            onChangeComplete={(color, e) => {
                                                const shouldClose = e.target.className !== 'hue-horizontal'
                                                this.handleChangeComplete(color)
                                            }}
                                            presetColors={randomProductColors}
                                        />
                                    </div>

                                    {customPalettesForm && Array.isArray(customPalettesForm.palettes) && customPalettesForm.palettes.length > 0 &&
                                        <div className={'toggler'} onClick={() => this.toggleDisplayPalettes()} title={trans('palettes')}>
                                            <IconPalette size={20} text={''} />
                                        </div>
                                    }

                                    {!hideGradients && typeof this.props.changeGradient === 'function' &&
                                        <div className={'toggler toggler-gradients'} onClick={() => this.toggleDisplayGradients()} title={trans('gradients')}>
                                            <IconGradients size={20} text={''} />
                                        </div>
                                    }
                                </div>
                            </div>
                        }
                    </GridCell>

                    <GridCell style={{ position: 'absolute', zIndex: 1000, top: '32px', right: '0px', display: 'grid' }}>
                        <div onClick={(e) => this.handleClick()}>
                            {displayColorPicker && displayPalettes &&
                                <div className={'wrapper'}>
                                    {selectedCustomPalette && selectedCustomPalette.colors &&
                                        <div className={'custom-palette-wrapper'}>
                                            <span title={selectedCustomPalette.name} className={'colors-title'}>{trans('brand_colors')}</span>
                                            <GithubPicker color={background} colors={selectedCustomPalette.colors} title={selectedCustomPalette.name} onChangeComplete={this.handleChangeComplete} />
                                        </div>
                                    }

                                    {/* {selectedPalette && selectedPalette.colors &&
                                        <div className={'palette-wrapper'}>
                                            <span title={selectedPalette.name} className={'colors-title'}>{trans('predefined_colors')}</span>
                                            <GithubPicker color={background} colors={selectedPalette.colors} title={selectedPalette.name} onChangeComplete={this.handleChangeComplete} />
                                        </div>
                                    } */}

                                    {/* {palettes &&
                                        <div className={'other-palette-wrapper'}>
                                            <span title={selectedPalette.name} className={'colors-title'}>{trans('predefined_colors')}</span>
                                            <SwatchesPicker
                                                color={background}
                                                onChangeComplete={this.handleChangeComplete}
                                                colors={palettes.map((palette: Palette) => {
                                                    return palette.colors
                                                })}
                                            />
                                        </div>
                                    } */}
                                </div>
                            }
                        </div>

                        {displayColorPicker && displayGradients && gradients &&
                            <div className={'wrapper shadow-box'} style={{ background: '#fff', padding: '5px', position: 'absolute', zIndex: 1000, top: '32px', right: '0px', display: 'grid' }}>
                                {gradients &&
                                    <Grid cols={'repeat(auto-fit, minmax(30px, 1fr))'}>
                                        {gradients.map((gradient, key) =>
                                            <GridCell key={`gradient-${key}`} className={'centered'}>
                                                <div className='clickable' title={`gradient-${gradient.id}`} onClick={() => { this.handleChangeGradient(gradient) }} style={{ width: '30px', height: '30px' }}>
                                                    <GradientSvg gradient={gradient} idx={key} width={30} height={30} />
                                                </div>
                                            </GridCell>
                                        )}
                                    </Grid>
                                }
                            </div>
                        }
                    </GridCell>
                    <GridCell />
                </Grid>
            </StyledField>
        )
    }
}

const StyledField = css(Field)({
    display: "inline-block",
    width: "100%",
    ' .toggler': {
        cursor: 'pointer',
        position: 'absolute',   //!important
        right: '5px',
        bottom: '5px',
        zIndex: 9999,
    },
    ' .toggler-gradients': {
        right: '30px',
    },
    ' .github-picker': {
        width: "auto !important",
    },
    " .swatches-picker": {
        width: "auto !important",
        ' div div div div': {
            padding: '4px !important',
            marginLeft: '1px',
        },
        ' div div div div div': {
            width: '20px !important',
        },
        ' div div div div div span div div': {
            margin: '-8px -9px !important',
        },
    },
    " .muk-form__group--invalid": {
        fontSize: "0",
    },
    " .m-input-color": {
        position: "relative",
        display: "inline-block",
        width: "100%",
        height: "40px",
        padding: "5px",
        backgroundColor: "#ffffff",
        userSelect: "none",
        transition: "border-color 0.2s",
        willChange: "border-color",
    },
    " .m-input-color:focus": {
        border: `${Theme.input_border_active} !important`,
    },
    " .m-input-color:active": {
        border: `${Theme.input_border_active} !important`,
    },
    " .muk-form__group--invalid .muk-colorpicker__colorbox": {
        borderColor: `${Theme.red} !important`,
    },
    ' .colors-title': {
        fontSize: '12px',
        padding: '4px 10px',
    },
    ' .sketch-picker': {
        float: 'right',
        width: 'auto !important',
        maxWidth: '250px',
    },
    ' .wrapper': {
        float: 'right',
        maxWidth: '220px',
        background: '#f0f8ff',
        padding: '4px',
        borderRadius: '4px',
    },
}, props => {
    return {
        " .m-input-color": {
            borderRadius: Theme.radius,
            borderColor: Theme.decoration,
            border: props["aria-invalid"] ? Theme.input_border_error : Theme.input_border,
        },
    };
});

export const Popover = css("div")({
    position: "fixed",
    inset: "0px",
});

export const Cover = css("div")({
    position: "fixed",
    top: "0px",
    right: "0px",
    bottom: "0px",
    left: "0px",
});

function mapStateToProps(state) {
    return {
        randomProduct: state.randomProduct,
        customPalettesForm: selectFormValues(state, 'customPalettesForm'),
        palettes: state.palettes,
        gradients: state.gradients,
        settingsForm: selectFormValues(state, 'settingsForm'),
    }
}

export default connect(mapStateToProps, {
})(TransparentColor)
